import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Button from '../atoms/Buttons'
import FormInput from '../atoms/Input/Form-Input'
import { FaAlignLeft } from 'react-icons/fa'
import { FaAlignCenter } from 'react-icons/fa'
import { FaAlignRight } from 'react-icons/fa'
import UploadIcon from '../../assets/icons/upload.svg'
import { UPDATE_BANNER } from '../../apollo/mutations/banner'
import { gql, useMutation, useQuery } from '@apollo/client'
import { LIST_BANNER } from '../../apollo/queries/banner'
import { BeatLoader } from 'react-spinners'
import { createToast } from '../../utils/Toast'
import TextArea from '../atoms/Input/TextArea'
import useSignedUrlUpload from '../../hooks/useSignedUrlUpload'
import { PreviewSecondBanner } from './PreviewBanner'
import {
  ImageFitControls,
  useBannerFocalDrag,
} from '../banner/BannerMediaFit'
import { pickBannerRowForSlot } from '../../utils/bannerListSlot'
import './style.css'

const Toast = createToast()

const HeroSectionSecond = ({ type: bannerType, listSlotIndex = 1 }) => {
  const listFilter = useMemo(
    () => (bannerType ? { type: bannerType } : null),
    [bannerType],
  )
  const title = bannerType === 'HERO'
  ? 'Banner'
  : (bannerType === 'CACTUS' || bannerType === 'BUY_SADDLE' ? 'Banner 1' : 'Banner 2');
  const imageRef = useRef(null)
  const { uploadFile } = useSignedUrlUpload()
  const [updateBanner, { loading: isLoading }] = useMutation(gql(UPDATE_BANNER))
  const { loading, data: BannerData } = useQuery(gql(LIST_BANNER), {
    skip: !listFilter,
    variables: { filter: listFilter },
    fetchPolicy: 'cache-and-network',
  })
  const bannerItem = useMemo(
    () => pickBannerRowForSlot(BannerData?.listBanner, listSlotIndex),
    [BannerData?.listBanner, listSlotIndex],
  )
  const [imageUrl, setImageUrl] = useState(null)
  const [videoUrl, setVideoUrl] = useState(null)
  const [imageUpload, setImageUpload] = useState(false)
  const [overlayColor,setOverlayColor] = useState('')
  let [selectedPosition, setSelectedPosition] = useState('justify-start')
  const [imageBgSize, setImageBgSize] = useState('cover')
  const [imageBgPosition, setImageBgPosition] = useState('center center')
  const { panHandlers: focalPanHandlers, isDragging: focalPanDragging } =
    useBannerFocalDrag(imageBgPosition, setImageBgPosition)
  const [error, setError] = useState('')

  const methods = useForm({
    defaultValues: {
      title: '',
      description: '',
      button_text: '',
      url: '',
      media: '',
      overlay: '',
    },
  })

  useEffect(() => {
    methods?.setValue('title', bannerItem?.contentHead)
    methods?.setValue('description', bannerItem?.contentDescription)
    methods?.setValue('button_text', bannerItem?.contentBtn)
    setImageUrl(bannerItem?.image)
    setVideoUrl(bannerItem?.video)
    setOverlayColor(bannerItem?.overlayColor)
    // methods?.setValue('overlay', bannerItem?.overlayColor)
    methods?.setValue('url', bannerItem?.btnUrl)
    setSelectedPosition(bannerItem?.position)
    setImageBgSize(bannerItem?.imageBackgroundSize ?? 'cover')
    setImageBgPosition(bannerItem?.imageBackgroundPosition ?? 'center center')
  }, [bannerItem])

  const handleClickImage = () => {
    imageRef.current.click()
  }

  const handleImage = async event => {
    const file = event.target.files[0]
    try {
      setImageUpload(true)
      const url = await uploadFile(file)
      if (file.type?.includes('image')) {
        setImageUrl(url)
        setVideoUrl(null)
      } else {
        setVideoUrl(url)
        setImageUrl(null)
      }
      setImageUpload(false)
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message?.toString() || 'Undefined error',
      })
      setImageUpload(false)
    }
  }

  const handlePosition = (e, alignment) => {
    setSelectedPosition(alignment)
  }

  const isValidUrl = url => {
    const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/
    return urlPattern.test(url)
  }

  const handleButtonClick = url => {
    const formattedUrl = url?.startsWith('http') ? url : `http://${url}`
    window.open(formattedUrl, '_blank')
  }

  const onSubmit = async data => {
    const banner_data = {
      ...(bannerItem?.id ? { id: bannerItem.id } : {}),
      type: bannerType,
      contentHead: data?.title,
      contentDescription: data?.description,
      contentBtn: data?.button_text,
      image: imageUrl ? imageUrl : null,
      video: videoUrl ? videoUrl : null,
      overlayColor: overlayColor,
      btnUrl: data?.url,
      position: selectedPosition,
      imageBackgroundSize: imageBgSize,
      imageBackgroundPosition: imageBgPosition,
    }
    try {
      await updateBanner({
        variables: { input: banner_data },
        refetchQueries: [
          { query: gql(LIST_BANNER), variables: { filter: listFilter } },
        ],
        awaitRefetchQueries: true,
      })
      Toast.fire({
        icon: 'success',
        title: bannerItem?.id ? 'Updated successfully' : 'Banner created in AppSync',
      })
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: error.message?.toString() || 'Update failed',
      })
    }
  }

  return (
    <div className="py-5" style={{ borderBottom: '1px solid #DFDFDF' }}>
      <details className="group">
        <summary className="flex cursor-pointer border items-center justify-between font-medium">
          <span className="change-font-family text-[#2B364B]">{title}</span>
          <span className="transition group-open:rotate-180">
            <svg
              fill="none"
              height="24"
              shapeRendering="geometricPrecision"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              width="24"
            >
              <path d="M6 9l6 6 6-6"></path>
            </svg>
          </span>
        </summary>

        <div className="w-full h-auto mt-20 pb-5  mx-auto">
          {!loading && !bannerItem?.id && listFilter ? (
            <p
              className="change-font-family mb-4 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-950"
              role="status"
            >
              No row yet for <strong className="font-mono">{bannerType}</strong> in
              this slot. Fill the form and click <strong>Save</strong> — AppSync will
              create the Banner (new id).
            </p>
          ) : null}
          <div
            onClick={handleClickImage}
            className="w-[100px] h-[100px] flex justify-center mb-2 items-center bg-[#d9d9d9] rounded-[10px]  cursor-pointer"
          >
            <img
              src={UploadIcon}
              alt="file"
              className="w-[30px] bg-[#d9d9d9] rounded-[10px]  cursor-pointer h-[30px]"
            />
          </div>

          <input
            type="file"
            ref={imageRef}
            accept="video/*,image/*"
            onChange={handleImage}
            style={{ display: 'none' }}
          />

           {loading || imageUpload ? (
            <div className="flex space-x-2 justify-center items-center bg-white h-screen">
              <span className="sr-only">Loading...</span>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="h-8 w-8 bg-[#2b364b] rounded-full animate-bounce"></div>
            </div>
          ) : (
            <PreviewSecondBanner
              bannerItem={bannerItem}
              isLoading={isLoading}
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              selectedPosition={selectedPosition}
              overlayColor={overlayColor}
              imageObjectFit={imageBgSize}
              imageObjectPosition={imageBgPosition}
              focalPanHandlers={focalPanHandlers}
              focalPanDragging={focalPanDragging}
            />
          )}

         <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className='mt-14'>
              <div>
                <FormInput
                  className="add_saddle_detail-form__field w-[100%] mx-auto mt-4"
                  label="Title"
                  name="title"
                  required
                  maxLength={23}
                  placeholder="Enter title"
                />
                <div className="!w-[100%] mt-4 mx-auto relative">
                  <TextArea
                    placeholder="description"
                    id="description"
                    name="description"
                    maxLength={166}
                    rows="4"
                    validations={{
                      required: {
                        value: true,
                        message: 'Description is required',
                      },
                    }}
                    className="w-[100%]  h-[167.582px] rounded-[30px]  mx-auto !mt-[31px] lg:mt-[68px] "
                  ></TextArea>
                  <label className="absolute change-font-family bg-[#fff] px-2 top-[-10px] left-2">
                    Description
                  </label>
                </div>
              </div>
              <div>
                <FormInput
                  className="add_saddle_detail-form__field w-[250px]  mt-4"
                  label="Button Text"
                  maxLength={12}
                  name="button_text"
                  required
                  placeholder="Enter button text"
                />
                <FormInput
                  className="add_saddle_detail-form__field w-[100%] mx-auto mt-4"
                  label="Url"
                  name="url"
                  required
                  placeholder="Enter URL"
                />
                {error && (
                  <div className="error-message" style={{ color: 'red' }}>
                    {error}
                  </div>
                )}
              </div>
              <div className="w-[100%] mx-auto mt-5">
                <div className="change-font-family content_text flex justify-start gap-x-5 items-center">
                  <label htmlFor="position">Position:</label>
                  <FaAlignLeft
                    value="left"
                    onClick={e => handlePosition(e, 'justify-start')}
                    className={`cursor-pointer ${
                      selectedPosition !== 'justify-start'
                        ? 'disabled opacity-40 bg-white'
                        : ''
                    }`}
                  />
                  <FaAlignCenter
                    value="center"
                    onClick={e => handlePosition(e, 'justify-center')}
                    className={`cursor-pointer ${
                      selectedPosition !== 'justify-center'
                        ? 'disabled opacity-40 bg-white'
                        : ''
                    }`}
                  />
                  <FaAlignRight
                    value="right"
                    onClick={e => handlePosition(e, 'justify-end')}
                    className={`cursor-pointer ${
                      selectedPosition !== 'justify-end'
                        ? 'disabled opacity-40 bg-white'
                        : ''
                    }`}
                  />

                  <label htmlFor="position">Color:</label>
                  <input
                    name="overlay"
                    value={overlayColor}
                    // {...methods.register('overlay')}
                    type={'color'}
                    id="style1"
                    onChange={e => setOverlayColor(e.target.value)}
                  />
                </div>
              </div>
              <ImageFitControls
                sizeValue={imageBgSize}
                positionValue={imageBgPosition}
                onSizeChange={setImageBgSize}
                onPositionChange={setImageBgPosition}
              />
              <Button
                type={'submit'}
                className="hero-section-save-btn cursor-pointer w-[181px] h-[56px]  mt-10 bg-[#2b364b] rounded-full border-none text-[#fff]"
              >
                {isLoading ? (
                  <BeatLoader color={'#ffffff'} size={10} />
                ) : (
                  'Save'
                )}
              </Button>
            </form>
          </FormProvider>
        </div> 
      </details>
    </div>
  )
}

export default HeroSectionSecond
